import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of, throwError } from 'rxjs';

import Resources from '@constants/resourses';

import { CarMakesAndModelType, SavedEVDetailsModel } from '../models/ev.interface';
import { CurrentUsageModel } from '../models/currentUsage.interface';

import { RestapiService } from './restapi.service';
import { SessionService } from './session.service';
import { catchError, map, retry, tap, timeout } from 'rxjs/operators';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { environment } from '@environments/environment';

@Injectable({
  providedIn: 'root'
})
export class EvService {
  private EV_URL = environment.api_url_ev;
  private $evDetails: BehaviorSubject<any> = new BehaviorSubject<any>(null);
  private carMakeModel: any = null;
  private uniqueCarMakes: CarMakesAndModelType[] = null;

  constructor(
    private restAPI: RestapiService,
    private sessionService: SessionService,
    private http: HttpClient,
  ) { }

  /**
    * @description - Get the updated EV details from the observable
    * @returns - Observable<any>
    * @example getEVDEtails
     */
  getEVStatus: Observable<any> = this.$evDetails.asObservable();
  /**
     * @description - Notify the EV details to all the subscribers
     * @param status - Details to be updated
     * @example updateEVStatus(evDetails)
     */
  updateEVStatus = (evDetail) => this.$evDetails.next(evDetail);

  /**
   * @description - Get EV details from the API
   * @param cPath - API path to get EV details
   * @returns - Observable<any>
   * @example getEVAPI('/v1/evDetails')
   */
  getEVAPI(cPath): Observable<any> {
    let httpOptions = {}
    httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json'
      })
    };
    if (navigator.onLine) {
      return this.http.get(this.EV_URL + cPath, httpOptions).pipe(
        timeout(30000),
        catchError((error) => {
          return throwError(error);
        }),
        map(this.restAPI.responseMapper),
      );
    } else {
      window.alert(this.restAPI.offlineMessage);
    }
  }

  /**
   * @description - Post EV details to the API
   * @param cPath - API path to post EV details
   * @param bodyObj - Body object to be sent in the request
   * @returns - Observable<any>
   * @example postAPIEV('/v1/evDetails', bodyObj)
   */
  postAPIEV(cPath: string, bodyObj): Observable<any> {
    let httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json'
      })
    };
    if (navigator.onLine) {
      return this.http.post<any>(this.EV_URL + cPath, bodyObj, httpOptions).pipe(
        timeout(30000),
        retry(1),
        tap((data) => {
        }),
        map(this.restAPI.responseMapper),
      );
    } else {
      window.alert(this.restAPI.offlineMessage);
    }
  }

  /**
   * @description - Save the EV details into session storage and update the saved details
   * @param evDetails - EV details to be saved
   * @param savedDetails - Saved details to be updated
   * @example savedEVDetails(evDetails, savedDetails)
   * @example savedEVDetails(evDetails)
   */
  savedEVDetails(evDetails, savedDetails?): void {
    const payload: SavedEVDetailsModel = {
      correlationId: this.sessionService.getItemFromSession(Resources.SessionStorageKeys.CorrelationId),
      model: evDetails.model,
      make: evDetails.make,
      evConsumptionKwh: evDetails?.evConsumption ? Number(evDetails?.evConsumption) : evDetails?.evConsumptionKwh ? Number(evDetails?.evConsumptionKwh) : 0,
      chargeAtHome: evDetails?.chargeAtHome || '',
      homeChargePercentage: Number(evDetails?.homeChargePercentage || evDetails?.homeChargePercentage || 0),
      offPeakChargePercentage: Number(evDetails?.offPeakChargePercentage || evDetails?.offPeakChargePercentage || 0),
      evMileage: Number(evDetails?.evMileage),
      evRegistrationYear: Number(evDetails?.evRegistrationYear),
      batteryCapacity: Number(evDetails?.batteryCapacity),
      isUserEdited: true
    };

    // If reg number is not present then no need to add a reg number in the payload
    if (evDetails?.regNumber) {
      payload.regNumber = evDetails.regNumber;
    }

    // Saved details are not present then add the saved details source in the payload
    payload['evSource'] = (savedDetails?.evDetails) ? 'savedDetails' : 'userInput';

    this.restAPI.postAPI('/v1/evDetails', payload).subscribe({
      next: (res) => {
        // Store the EV details in session storage
        this.sessionService.setData(Resources.SessionStorageKeys.EVDETAILS, (savedDetails ? savedDetails.evDetails : evDetails.getRawValue()));
        // EV details update into saved details and store into session storage
        if (savedDetails) {
          savedDetails['evDetails'] = payload;
          this.sessionService.setData(Resources.SessionStorageKeys.SavedDetail, savedDetails);
        }

        // Update the EV details observable
        this.updateEVStatus(null);
      },
      error: (error) => {
        console.log('Save EV Details API Error', error);
      }
    })
  }

  /**
   * @description - Get car make and model from the API
   * @param cPath - API path to get car make and model 
   * @returns - Observable<any>
   * @example getCarMakeAndModel('/v1/evDetails')
   */
  getCarMakeAndModel(cPath): Observable<any> {
    if (this.carMakeModel) {
      return of(this.carMakeModel);
    }
    return this.getEVAPI(cPath).pipe(
      map((response) => {
        this.carMakeModel = response.data;
        return response.data;  // Return data extracted from the response
      }), catchError((error) => {
        console.log('ERROR: ', error);
        return of(null);  // Return null or handle the error appropriately
      })
    );
  }

  /**
   * @description - Get unique, sorted car makes deduped from the full make/model list. Result is cached in-memory so the dedup only runs once per session.
   * @param makeAndModel - Full list of car make/model entries (from getCarMakeAndModel)
   * @returns - CarMakesAndModelType[]
   * @example getUniqueCarMakes(makeAndModelList)
   */
  getUniqueCarMakes(makeAndModel: CarMakesAndModelType[]): CarMakesAndModelType[] {
    if (this.uniqueCarMakes) {
      return this.uniqueCarMakes;
    }
    const uniqueMakes = new Map<string, CarMakesAndModelType>();
    (makeAndModel || []).forEach(carMake => {
      if (carMake?.model && !uniqueMakes.has(carMake.make)) {
        uniqueMakes.set(carMake.make, carMake);
      }
    });
    this.uniqueCarMakes = Array.from(uniqueMakes.values()).sort((a, b) => a.displayMake.localeCompare(b.displayMake));
    return this.uniqueCarMakes;
  }

  // activity track with transaction API
  activityTrackWithTransactionAPI(currentUsage: CurrentUsageModel, email: string, productEVConfig: any): void {
    const bodyObj = {
      addressLine: this.sessionService.getData(Resources.SessionStorageKeys.SelectedAddress).addressText,
      correlationId: this.sessionService.getItemFromSession(Resources.SessionStorageKeys.CorrelationId),
      fuelType: currentUsage.electricity && currentUsage.gas ? 'B' : currentUsage.electricity ? 'E' : 'G',
      mpan: currentUsage?.mpan,
      mprn: currentUsage?.mprn,
      email: email,
      productSubscriptionId: productEVConfig?.data?.product_subscription_id
    };
    this.restAPI.postAPI('/v1/users/product/transaction', bodyObj).subscribe({
      next: (data) => {
      }, error: (err) => {
        console.log('Error in transaction API: ', err);
      }
    })
  }
}
